/**
 * Image Resize & Drag Module
 * Click-to-select with 8 resize handles, proportional corner resize,
 * drag-to-reposition, and floating alignment toolbar.
 */

let activeOverlay = null;
let activeImg = null;
let editorEl = null;
let pageRef = null;

const HANDLE_SIZE = 10;
const HANDLES = ['nw','n','ne','e','se','s','sw','w'];

function createOverlay(img) {
    removeOverlay();
    activeImg = img;

    const overlay = document.createElement('div');
    overlay.className = 'img-resize-overlay';
    overlay.contentEditable = 'false';

    // 8 resize handles
    HANDLES.forEach(pos => {
        const h = document.createElement('div');
        h.className = `img-resize-handle img-resize-handle--${pos}`;
        h.dataset.handle = pos;
        overlay.appendChild(h);
    });

    // Alignment toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'img-align-toolbar';
    toolbar.innerHTML = `
        <button data-align="left" title="Float left">&#8676;</button>
        <button data-align="center" title="Center">&#8596;</button>
        <button data-align="right" title="Float right">&#8677;</button>
        <button data-align="full" title="Full width">&#10231;</button>
        <button data-align="reset" title="Reset">&#8634;</button>
        <span class="img-align-toolbar-sep" aria-hidden="true"></span>
        <button data-action="delete" title="Delete image" class="img-align-toolbar-delete">&#128465;</button>
    `;
    overlay.appendChild(toolbar);

    // Position overlay over the image
    positionOverlay(overlay, img);

    editorEl.appendChild(overlay);
    activeOverlay = overlay;

    // Handle resize drag
    overlay.addEventListener('mousedown', onHandleMouseDown);
    // Handle alignment clicks
    toolbar.addEventListener('click', onAlignClick);
    // Handle image drag (hold 150ms)
    img.addEventListener('mousedown', onImageDragStart);
}

function positionOverlay(overlay, img) {
    const rect = img.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();
    overlay.style.left = (rect.left - editorRect.left + editorEl.scrollLeft) + 'px';
    overlay.style.top = (rect.top - editorRect.top + editorEl.scrollTop) + 'px';
    overlay.style.width = rect.width + 'px';
    overlay.style.height = rect.height + 'px';
}

function removeOverlay() {
    if (activeOverlay) {
        activeOverlay.remove();
        activeOverlay = null;
    }
    if (activeImg) {
        activeImg.removeEventListener('mousedown', onImageDragStart);
        activeImg = null;
    }
}

function deleteActiveImage() {
    if (!activeImg) return;
    const figure = activeImg.closest('figure');
    const target = figure || activeImg;
    if (target && target.parentNode) target.parentNode.removeChild(target);
    removeOverlay();
    markDirty();
}

function onAlignClick(e) {
    const delBtn = e.target.closest('[data-action="delete"]');
    if (delBtn && activeImg) {
        e.preventDefault();
        e.stopPropagation();
        deleteActiveImage();
        return;
    }
    const btn = e.target.closest('[data-align]');
    if (!btn || !activeImg) return;
    e.preventDefault();
    e.stopPropagation();

    const figure = activeImg.closest('figure');
    // If inside a table cell or column without a figure wrapper, apply styles directly to img
    const inCell = activeImg.closest('td, th, .wp-column');
    const target = figure || (inCell ? activeImg : activeImg.parentElement);
    const align = btn.dataset.align;

    // Remove all alignment classes and inline styles
    const classes = ['wp-image-float-left', 'wp-image-center', 'wp-image-float-right', 'wp-image-full-width'];
    classes.forEach(c => target.classList.remove(c));
    activeImg.style.float = '';
    activeImg.style.display = '';
    activeImg.style.margin = '';
    if (align !== 'full') activeImg.style.width = '';

    if (align === 'left') {
        if (figure) { figure.classList.add('wp-image-float-left'); }
        else { activeImg.style.float = 'left'; activeImg.style.margin = '0 12px 8px 0'; }
    } else if (align === 'center') {
        if (figure) { figure.classList.add('wp-image-center'); }
        else { activeImg.style.display = 'block'; activeImg.style.margin = '8px auto'; }
    } else if (align === 'right') {
        if (figure) { figure.classList.add('wp-image-float-right'); }
        else { activeImg.style.float = 'right'; activeImg.style.margin = '0 0 8px 12px'; }
    } else if (align === 'full') {
        if (figure) { figure.classList.add('wp-image-full-width'); }
        else { activeImg.style.width = '100%'; activeImg.style.display = 'block'; }
    }
    // 'reset' just removes all classes (already done above)

    markDirty();
    // Reposition overlay after alignment change
    requestAnimationFrame(() => {
        if (activeOverlay && activeImg) positionOverlay(activeOverlay, activeImg);
    });
}

// --- Resize ---
let resizeState = null;

function onHandleMouseDown(e) {
    const handle = e.target.closest('[data-handle]');
    if (!handle || !activeImg) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = activeImg.getBoundingClientRect();
    resizeState = {
        handle: handle.dataset.handle,
        startX: e.clientX,
        startY: e.clientY,
        startW: rect.width,
        startH: rect.height,
        aspect: rect.width / rect.height
    };

    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e) {
    if (!resizeState || !activeImg) return;
    e.preventDefault();

    const dx = e.clientX - resizeState.startX;
    const dy = e.clientY - resizeState.startY;
    const h = resizeState.handle;
    let newW = resizeState.startW;
    let newH = resizeState.startH;

    // Corner handles: proportional
    if (h === 'se') {
        newW = Math.max(50, resizeState.startW + dx);
        newH = newW / resizeState.aspect;
    } else if (h === 'sw') {
        newW = Math.max(50, resizeState.startW - dx);
        newH = newW / resizeState.aspect;
    } else if (h === 'ne') {
        newW = Math.max(50, resizeState.startW + dx);
        newH = newW / resizeState.aspect;
    } else if (h === 'nw') {
        newW = Math.max(50, resizeState.startW - dx);
        newH = newW / resizeState.aspect;
    }
    // Side handles: stretch independently
    else if (h === 'e') {
        newW = Math.max(50, resizeState.startW + dx);
    } else if (h === 'w') {
        newW = Math.max(50, resizeState.startW - dx);
    } else if (h === 's') {
        newH = Math.max(50, resizeState.startH + dy);
    } else if (h === 'n') {
        newH = Math.max(50, resizeState.startH - dy);
    }

    activeImg.style.width = Math.round(newW) + 'px';
    activeImg.style.height = Math.round(newH) + 'px';

    if (activeOverlay) positionOverlay(activeOverlay, activeImg);
}

function onResizeEnd(e) {
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', onResizeEnd);
    resizeState = null;
    markDirty();
}

// --- Image Drag to Reposition ---
let dragState = null;

function onImageDragStart(e) {
    if (resizeState) return; // Don't drag while resizing
    if (e.target.closest('.img-resize-handle') || e.target.closest('.img-align-toolbar')) return;

    const img = e.target.closest('img');
    if (!img) return;

    // 150ms hold before drag starts
    const startX = e.clientX;
    const startY = e.clientY;
    let holdTimer = setTimeout(() => {
        dragState = { img, ghost: null };
        startDrag(img, e);
    }, 150);

    const onMove = (ev) => {
        if (Math.abs(ev.clientX - startX) > 5 || Math.abs(ev.clientY - startY) > 5) {
            if (!dragState) {
                clearTimeout(holdTimer);
                cleanup();
            }
        }
        if (dragState && dragState.ghost) {
            dragState.ghost.style.left = (ev.clientX - 30) + 'px';
            dragState.ghost.style.top = (ev.clientY - 30) + 'px';
        }
    };

    const onUp = (ev) => {
        clearTimeout(holdTimer);
        if (dragState) {
            finishDrag(ev);
        }
        cleanup();
    };

    function cleanup() {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
}

function startDrag(img, e) {
    removeOverlay();
    const ghost = document.createElement('img');
    ghost.src = img.src;
    ghost.className = 'img-drag-ghost';
    ghost.style.width = '60px';
    ghost.style.height = '60px';
    ghost.style.left = (e.clientX - 30) + 'px';
    ghost.style.top = (e.clientY - 30) + 'px';
    document.body.appendChild(ghost);
    dragState.ghost = ghost;
    img.style.opacity = '0.3';
}

function finishDrag(e) {
    if (!dragState) return;
    const { img, ghost } = dragState;

    if (ghost) ghost.remove();
    img.style.opacity = '';

    // Find drop position
    const range = document.caretRangeFromPoint
        ? document.caretRangeFromPoint(e.clientX, e.clientY)
        : null;

    if (range && editorEl.contains(range.startContainer)) {
        // Move the figure or img to the new position
        const figure = img.closest('figure') || img;
        const target = range.startContainer;

        // Find the nearest block-level container to drop into
        // This handles drops inside tables, columns, or the root editor
        let dropContainer = target;
        // Walk up to find an element node
        if (dropContainer.nodeType === 3) dropContainer = dropContainer.parentNode;

        // Find the appropriate insertion point
        let block = target;
        // Walk up to nearest block-level parent (td, wp-column, or direct editor child)
        while (block && block !== editorEl) {
            const parent = block.parentNode;
            if (!parent) break;
            // Stop at cell, column, or editor boundary
            if (parent === editorEl || parent.nodeName === 'TD' || parent.nodeName === 'TH' ||
                parent.classList?.contains('wp-column')) {
                break;
            }
            block = parent;
        }
        if (block && block !== figure) {
            block.parentNode.insertBefore(figure, block.nextSibling);
        }
    }

    dragState = null;
    markDirty();

    // Re-select the moved image
    requestAnimationFrame(() => createOverlay(img));
}

function markDirty() {
    if (pageRef) {
        pageRef.blogDirty = true;
        if (pageRef.activeBlogPost) {
            pageRef.activeBlogPost.content_html = editorEl.innerHTML;
        }
        if (pageRef.renderBlogDirtyUI) pageRef.renderBlogDirtyUI();
    }
}

function onEditorClick(e) {
    const img = e.target.closest('.blog-content-editor img');
    if (img && editorEl.contains(img)) {
        e.preventDefault();
        createOverlay(img);
        return;
    }
    // Click outside image — deselect
    if (!e.target.closest('.img-resize-overlay')) {
        removeOverlay();
    }
}

function onEditorKeydown(e) {
    if (activeOverlay && activeImg && (e.key === 'Delete' || e.key === 'Backspace')) {
        e.preventDefault();
        deleteActiveImage();
        return;
    }
    // Deselect image on any typing
    if (activeOverlay && !e.target.closest('.img-align-toolbar')) {
        removeOverlay();
    }
}

export function init(editor, page) {
    editorEl = editor;
    pageRef = page;

    editor.addEventListener('click', onEditorClick);
    editor.addEventListener('keydown', onEditorKeydown);

    // Reposition overlay on scroll/resize
    const reposition = () => {
        if (activeOverlay && activeImg) positionOverlay(activeOverlay, activeImg);
    };
    editor.addEventListener('scroll', reposition);
    window.addEventListener('resize', reposition);
}
